import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, downloadCsv } from '../lib/api';
import { Badge, Button, Card, EmptyState, Spinner } from '../components/ui';
import { ResponseView } from '../components/ResponseView';
import { relative } from './FormList';

export function Responses() {
  const { formId = '' } = useParams();
  // The open response is held by id rather than by row, so the drawer's own
  // older/newer buttons can move to a response this page has not loaded a row for.
  const [selected, setSelected] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const responses = useQuery({
    queryKey: ['responses', formId],
    queryFn: () => api.responses.list(formId),
  });

  const questionRefs = Array.from(
    new Set(responses.data?.data.flatMap((r) => Object.keys(r.answers)) ?? []),
  );

  async function exportCsv() {
    setExporting(true);
    try { await downloadCsv(formId, `${formId}-responses.csv`); }
    finally { setExporting(false); }
  }

  if (responses.isLoading) return <div className="p-8"><Spinner label="Loading responses" /></div>;

  const rows = responses.data?.data ?? [];

  return (
    <div className="p-6">
      <div className="mb-4 flex items-center gap-3">
        <div>
          <h1 className="text-lg font-semibold">Responses</h1>
          <p className="text-sm text-[var(--ff-text-muted)]">
            {rows.length} {rows.length === 1 ? 'response' : 'responses'}
          </p>
        </div>
        <Button className="ml-auto" onClick={exportCsv} disabled={exporting || rows.length === 0}>
          {exporting ? 'Exporting…' : 'Export CSV'}
        </Button>
      </div>

      {rows.length === 0 ? (
        <Card>
          <EmptyState
            title="No responses yet"
            description="Publish the form and share its link or embed it in a page. Responses appear here as they arrive."
          />
        </Card>
      ) : (
        <Card className="overflow-hidden">
          {/* Wide tables scroll inside their own container; the page never scrolls sideways. */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <caption className="ff-sr">
                Responses, most recent first. Select a row to see the full submission.
              </caption>
              <thead className="border-b border-[var(--ff-border)] bg-[var(--ff-surface-raised)]">
                <tr>
                  <th scope="col" className="px-3 py-2 text-left font-semibold">#</th>
                  <th scope="col" className="px-3 py-2 text-left font-semibold">Submitted</th>
                  <th scope="col" className="px-3 py-2 text-left font-semibold">Channel</th>
                  {questionRefs.slice(0, 4).map((ref) => (
                    <th key={ref} scope="col"
                        className="px-3 py-2 text-left font-mono text-[11px] font-semibold">
                      {ref}
                    </th>
                  ))}
                  <th scope="col" className="px-3 py-2 text-left font-semibold">Score</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.id}
                    onClick={() => setSelected(row.id)}
                    className="cursor-pointer border-b border-[var(--ff-border)] last:border-0
                               hover:bg-[var(--ff-surface-raised)]"
                  >
                    <td className="px-3 py-2 tabular-nums text-[var(--ff-text-muted)]">
                      {row.sequenceNumber}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2">{relative(row.submittedUtc)}</td>
                    <td className="px-3 py-2"><Badge>{row.channel}</Badge></td>
                    {questionRefs.slice(0, 4).map((ref) => (
                      <td key={ref} className="max-w-40 truncate px-3 py-2">
                        {formatAnswer(row.answers[ref])}
                      </td>
                    ))}
                    <td className="px-3 py-2">
                      {row.score
                        ? <Badge tone={row.score.passed ? 'success' : 'danger'}>
                            {Math.round(row.score.percentage)}%
                          </Badge>
                        : <span className="text-[var(--ff-text-muted)]">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {selected && (
        <ResponseDrawer
          responseId={selected}
          onClose={() => setSelected(null)}
          onNavigate={setSelected}
        />
      )}
    </div>
  );
}

/**
 * The drawer around the shared answer view.
 *
 * It used to render the answers itself, as `questionRef: value` straight from the
 * document. The table above is unchanged — it is a good way to scan one form's responses —
 * but reading a single one now goes through the same component the inbox uses, so question
 * titles, choice labels, repeating groups and PII masking behave identically wherever a
 * response is opened.
 */
function ResponseDrawer({
  responseId, onClose, onNavigate,
}: {
  responseId: string;
  onClose: () => void;
  onNavigate: (id: string) => void;
}) {
  // Escape closes the drawer. Without it the only way out is a mouse trip to one button.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-30 flex justify-end bg-black/40"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Response"
        className="flex h-full w-full max-w-2xl flex-col bg-[var(--ff-surface)] shadow-xl"
      >
        <ResponseView responseId={responseId} onClose={onClose} onNavigate={onNavigate} />
      </div>
    </div>
  );
}

function formatAnswer(value: unknown): string {
  if (value == null) return '';
  if (Array.isArray(value)) return value.join('; ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}
